import { memo, useCallback, useMemo, useState } from 'react';

import { type ShareUrlUpdatedColorRole } from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';
import { CustomColorPickerInput } from '@air/component-custom-color-picker-input';
import { ChevronDownIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Popover } from '@air/primitive-popover';
import { cn } from '@air/utils-tailwind';

import { ShareDesignColorRowBrandOption } from '~/components/ShareModal/components/ShareDesignColorRowBrandOption';
import { ShareModalRow } from '~/components/ShareModal/components/ShareModalRow';
import { type BrandColorEntry } from '~/components/ShareModal/shareLinkDesignResolution';
import { UpsellFlashIconButton } from '~/components/SharePrivateContentModal/shared/UpsellFlashIconButton';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';

export type ShareCustomColors = NonNullable<NonNullable<ShortUrlResponse['presentationSettings']>['customColors']>;

export type ShareDesignColorRole = {
  analyticsRole: ShareUrlUpdatedColorRole;
  /** Hex shown and previewed when the share link has no stored color for this role. */
  defaultHex: string;
  label: string;
  /** The stored role this row reads its committed value from. */
  readKey: keyof ShareCustomColors;
  /** data-testid for the swatch trigger button. */
  testId: string;
  /** All stored roles written on commit — "Buttons & headlines" authors two roles with one hex. */
  writeKeys: readonly (keyof ShareCustomColors)[];
};

export type ShareDesignColorRowProps = {
  /**
   * Brand-kit color entries offered as quick-pick swatches (grouped by their source field) above
   * the custom picker. Empty, the popover renders the custom picker alone.
   */
  brandColors: BrandColorEntry[];
  disabled: boolean;
  /** Resolved hex for the swatch: transient preview ?? stored ?? default. The parent resolves it. */
  displayHex: string;
  /** True when the share link has a stored custom color for this role. */
  isCustomColor: boolean;
  /** Called when the popover closes without saving, so the parent can drop transient preview state. */
  onDiscard: (role: ShareDesignColorRole) => void;
  onPreviewChange: (role: ShareDesignColorRole, hex: string) => void;
  onReset: (role: ShareDesignColorRole) => void;
  onSave: (role: ShareDesignColorRole, hex: string) => void;
  /**
   * Called when a brand quick-pick swatch is chosen. Distinct from `onSave` on purpose: a brand
   * pick commits a brand-kit REFERENCE (the entry's full id triple), never the entry's plain hex —
   * a plain-hex write would leave nothing to rehydrate the kit selection or swatch label from on
   * reload. Required (not optional) so a consumer can't offer brand swatches without deciding what
   * a brand pick commits.
   */
  onSaveBrandColor: (role: ShareDesignColorRole, entry: BrandColorEntry) => void;
  role: ShareDesignColorRole;
  /**
   * The brand value id backing the stored color when it's a contextSource ref — drives the trigger
   * label and the selected checkmark by identity. `undefined` for plain-hex/absent stored colors.
   */
  selectedBrandColorId: string | undefined;
  /**
   * Controls ONLY the plan-gate upsell affordance — pass the plan-gate-only value (e.g.
   * `!designSettingsEnabled`), independent of `disabled` (which also covers the in-flight save
   * state). Keeping these separate avoids flashing the upsell on every row during a save.
   */
  showUpsell: boolean;
};

export const ShareDesignColorRow = memo(
  ({
    brandColors,
    disabled,
    displayHex,
    isCustomColor,
    onDiscard,
    onPreviewChange,
    onReset,
    onSave,
    onSaveBrandColor,
    role,
    selectedBrandColorId,
    showUpsell,
  }: ShareDesignColorRowProps) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        setIsPopoverOpen(open);

        if (!open) {
          onDiscard(role);
        }
      },
      [onDiscard, role],
    );

    const handlePreviewChange = useCallback((hex: string) => onPreviewChange(role, hex), [onPreviewChange, role]);

    const handleSave = useCallback(
      (hex: string) => {
        onSave(role, hex);
        setIsPopoverOpen(false);
      },
      [onSave, role],
    );

    const handleReset = useCallback(() => {
      onReset(role);
      setIsPopoverOpen(false);
    }, [onReset, role]);

    // A brand swatch pick commits the brand-kit REFERENCE, not its hex (see onSaveBrandColor's
    // prop doc) — the consumer decides the ref shape; the row just closes like a manual save.
    const handleSelectBrandColor = useCallback(
      (entry: BrandColorEntry) => {
        onSaveBrandColor(role, entry);
        setIsPopoverOpen(false);
      },
      [onSaveBrandColor, role],
    );

    /**
     * The brand entry labelling the trigger and carrying the popover's selected checkmark.
     * Identity wins: a stored contextSource ref names its entry exactly via
     * `selectedBrandColorId`. Hex matching stays as the fallback so stored plain hexes (manual
     * picks that happen to equal a brand color, and brand picks saved before refs existed) still
     * label as their brand entry.
     */
    const matchedBrandEntry = useMemo(() => {
      const entryById = selectedBrandColorId
        ? brandColors.find((entry) => entry.id === selectedBrandColorId)
        : undefined;

      return entryById ?? brandColors.find((entry) => entry.hex.toLowerCase() === displayHex.toLowerCase());
    }, [brandColors, displayHex, selectedBrandColorId]);

    // Group brand colors by their source field so the popover renders one labelled section per
    // brand field (e.g. "COLORS") instead of a flat list — mirrors the font row's brand grouping.
    const brandColorGroups = useMemo(() => {
      const groups: { fieldName: string; colors: BrandColorEntry[] }[] = [];

      for (const entry of brandColors) {
        const group = groups.find((candidate) => candidate.fieldName === entry.fieldName);

        if (group) {
          group.colors.push(entry);
        } else {
          groups.push({ fieldName: entry.fieldName, colors: [entry] });
        }
      }

      return groups;
    }, [brandColors]);

    return (
      <ShareModalRow label={role.label}>
        {showUpsell && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>}
        <Popover
          align="end"
          className="w-[280px]"
          onOpenChange={handleOpenChange}
          open={isPopoverOpen}
          size="large"
          trigger={
            <button
              aria-expanded={isPopoverOpen}
              aria-haspopup="dialog"
              aria-label={`${role.label} color, ${displayHex}`}
              className="flex h-7 w-[210px] items-center justify-between gap-1.5 rounded-6 border ads:border-input border-gray-5 ads:bg-base bg-gray-1 px-2 ads:hover:border-hover-input hover:border-gray-7 disabled:cursor-not-allowed disabled:opacity-50 ads:disabled:hover:border-input disabled:hover:border-gray-5"
              data-testid={role.testId}
              disabled={disabled}
              type="button"
            >
              <span className="flex min-w-0 flex-1 items-center gap-1.5">
                <span
                  className="size-2.5 shrink-0 rounded-full border border-black/10"
                  style={{ backgroundColor: displayHex }}
                />
                {matchedBrandEntry ? (
                  // A brand label alone — it and the hex chip can't both fit the 140px trigger, and
                  // the label is the richer identity. The hex stays in the aria-label + popover.
                  <span className="min-w-0 truncate ads:text-primary text-12 text-gray-12">
                    {matchedBrandEntry.label}
                  </span>
                ) : (
                  <>
                    <span
                      className={cn(
                        'text-12',
                        isCustomColor ? 'ads:text-primary text-gray-12' : 'ads:text-metadata text-gray-9',
                      )}
                    >
                      {isCustomColor ? 'Custom' : 'Default'}
                    </span>
                    <span className="shrink-0 font-mono ads:text-metadata text-10 text-gray-8 uppercase">
                      {displayHex}
                    </span>
                  </>
                )}
              </span>
              <ChevronDownIcon className="shrink-0 ads:text-icon-subtle text-gray-9" size="xs" />
            </button>
          }
        >
          <div className="flex flex-col gap-2">
            {brandColorGroups.length > 0 && (
              // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
              <div className="flex flex-col gap-1" data-testid={`${role.testId}_BRAND_COLORS`}>
                {brandColorGroups.map((group) => (
                  <div className="flex flex-col" key={group.fieldName}>
                    <div className="px-1 pt-1 pb-0.5 font-semibold ads:text-metadata text-10 text-gray-9 uppercase tracking-wide">
                      {group.fieldName}
                    </div>
                    {group.colors.map((entry) => (
                      <ShareDesignColorRowBrandOption
                        entry={entry}
                        isSelected={entry.id === matchedBrandEntry?.id}
                        key={entry.id}
                        onSelect={handleSelectBrandColor}
                        testId={`${role.testId}_BRAND_COLOR_OPTION`}
                      />
                    ))}
                  </div>
                ))}
                <hr className="h-px border-0 bg-gray-4" />
              </div>
            )}
            <CustomColorPickerInput
              defaultColor={displayHex}
              inputLabel={`${role.label} hex value`}
              onChange={handlePreviewChange}
              onClear={isCustomColor ? handleReset : undefined}
              onSave={handleSave}
            />
            {isCustomColor && (
              <Button
                appearance="ghost"
                color="gray"
                data-testid="SHARE_DESIGN_COLOR_RESET_BUTTON"
                onClick={handleReset}
                size="small"
                type="button"
              >
                Reset to default
              </Button>
            )}
          </div>
        </Popover>
      </ShareModalRow>
    );
  },
);

ShareDesignColorRow.displayName = 'ShareDesignColorRow';
